import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Key, 
  Cloud, 
  Server, 
  Save,
  Eye,
  EyeOff,
  CheckCircle,
  XCircle,
  ExternalLink,
  RefreshCw,
  Link as LinkIcon,
  Wifi
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SettingsPage = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    fac_app_id: '',
    fac_app_secret: '',
    openweather_api_key: ''
  });
  const [currentSettings, setCurrentSettings] = useState(null);
  const [facStatus, setFacStatus] = useState(null);
  const [showSecrets, setShowSecrets] = useState({
    fac_app_secret: false,
    openweather_api_key: false
  });
  
  // FAC Cloud state
  const [facDevices, setFacDevices] = useState([]);
  const [loadingFacDevices, setLoadingFacDevices] = useState(false);
  const [bindDeviceNo, setBindDeviceNo] = useState('');
  const [bindDeviceName, setBindDeviceName] = useState('');
  const [binding, setBinding] = useState(false);

  useEffect(() => {
    fetchSettings();
    fetchFacStatus();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setCurrentSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFacStatus = async () => {
    try {
      const response = await axios.get(`${API}/fac/status`);
      setFacStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch FAC status:', error);
    }
  };

  const handleSave = async (type) => {
    setSaving(true);
    try {
      const updateData = {};
      
      if (type === 'fac') {
        if (settings.fac_app_id) updateData.fac_app_id = settings.fac_app_id;
        if (settings.fac_app_secret) updateData.fac_app_secret = settings.fac_app_secret;
      } else if (type === 'weather') {
        if (settings.openweather_api_key) updateData.openweather_api_key = settings.openweather_api_key;
      }

      await axios.put(`${API}/settings`, updateData);
      toast.success('Settings saved successfully! / 設定已儲存！');
      fetchSettings();
      fetchFacStatus();
      
      if (type === 'fac') {
        setSettings(prev => ({ ...prev, fac_app_id: '', fac_app_secret: '' }));
      } else if (type === 'weather') {
        setSettings(prev => ({ ...prev, openweather_api_key: '' }));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const testFacConnection = async () => {
    try {
      await axios.post(`${API}/fac/token`);
      toast.success('FAC Cloud connection successful! / FAC 雲端連線成功！');
      fetchFacStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'FAC Cloud connection failed');
    }
  };

  const fetchFacDevices = async () => {
    setLoadingFacDevices(true);
    try {
      const response = await axios.get(`${API}/fac/devices`);
      setFacDevices(response.data.devices || []);
      if (response.data.message) {
        toast.info(response.data.message);
      } else {
        toast.success(`Found ${response.data.devices?.length || 0} devices / 找到 ${response.data.devices?.length || 0} 個設備`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fetch FAC devices');
    } finally {
      setLoadingFacDevices(false);
    }
  };

  const handleBindDevice = async () => {
    if (!bindDeviceNo.trim()) {
      toast.error('Please enter device serial number');
      return;
    }
    
    setBinding(true);
    try {
      const response = await axios.post(`${API}/fac/devices/bind`, null, {
        params: {
          device_no: bindDeviceNo,
          device_name: bindDeviceName || undefined
        }
      });
      toast.success('Device bound successfully! / 設備綁定成功！');
      setBindDeviceNo('');
      setBindDeviceName('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to bind device');
    } finally {
      setBinding(false);
    }
  };

  const isFacConnected = facStatus?.connected || currentSettings?.has_fac_credentials;

  return (
    <div className="min-h-screen bg-fac-dark pt-16 pb-24 md:pb-8">
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-6">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/app')}
            className="text-white hover:text-fac-green"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-heading font-bold text-2xl text-white" data-testid="settings-title">
              SETTINGS
            </h1>
            <p className="text-fac-green font-chinese">設定</p>
          </div>
        </div>

        {/* User Info */}
        <Card className="bg-fac-card border-border mb-6">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-fac-green/10 flex items-center justify-center">
                <span className="text-xl font-bold text-fac-green">
                  {user?.name?.[0]?.toUpperCase() || 'U'}
                </span>
              </div>
              <div className="flex-1">
                <p className="text-white font-medium">{user?.name}</p>
                <p className="text-sm text-muted-foreground">{user?.email}</p>
                <span className={`text-xs px-2 py-0.5 rounded ${
                  user?.role === 'admin' ? 'bg-fac-green/20 text-fac-green' : 'bg-fac-blue/20 text-fac-blue'
                }`}>
                  {user?.role?.toUpperCase()}
                </span>
              </div>
              {/* FAC Status */}
              {facStatus && (
                <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
                  facStatus.connected ? 'bg-fac-green/10 text-fac-green' : 'bg-fac-yellow/10 text-fac-yellow'
                }`}>
                  <Wifi className="w-4 h-4" />
                  <span className="text-sm font-medium">
                    {facStatus.connected ? 'FAC Connected' : 'FAC Offline'}
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Settings Tabs */}
        <Tabs defaultValue="api-keys" className="space-y-6">
          <TabsList className="bg-fac-card border border-border">
            <TabsTrigger value="api-keys" className="data-[state=active]:bg-fac-green data-[state=active]:text-black">
              <Key className="w-4 h-4 mr-2" />
              API Keys
            </TabsTrigger>
            <TabsTrigger value="fac-cloud" className="data-[state=active]:bg-fac-green data-[state=active]:text-black">
              <Server className="w-4 h-4 mr-2" />
              FAC Cloud
            </TabsTrigger>
          </TabsList>

          <TabsContent value="api-keys" className="space-y-6">
            {/* OpenWeatherMap API */}
            <Card className="bg-fac-card border-border">
              <CardHeader>
                <CardTitle className="font-heading text-white uppercase flex items-center gap-2">
                  <Cloud className="w-5 h-5 text-fac-blue" />
                  OpenWeatherMap API
                </CardTitle>
                <CardDescription className="font-chinese">
                  天氣 API 金鑰 - Get real weather data for your farm location
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-2 p-3 bg-fac-paper rounded-lg">
                  {currentSettings?.has_weather_key ? (
                    <>
                      <CheckCircle className="w-5 h-5 text-fac-green" />
                      <span className="text-white">API Key configured (System)</span>
                      <span className="text-muted-foreground ml-2 font-mono text-sm">
                        {currentSettings.openweather_api_key_masked || 'b5cd...0565'}
                      </span>
                    </>
                  ) : (
                    <>
                      <XCircle className="w-5 h-5 text-fac-yellow" />
                      <span className="text-muted-foreground">No API key configured (using demo data)</span>
                    </>
                  )}
                </div>

                <div className="p-4 bg-fac-paper border border-border rounded-lg">
                  <h4 className="text-white font-medium mb-2">How to add your own API key / 如何新增您自己的 API 金鑰:</h4>
                  <ol className="text-sm text-muted-foreground space-y-1 font-chinese">
                    <li>1. Sign up at <a href="https://openweathermap.org/api" target="_blank" rel="noopener noreferrer" className="text-fac-blue hover:underline inline-flex items-center gap-1">OpenWeatherMap <ExternalLink className="w-3 h-3" /></a> (free)</li>
                    <li>2. Get your API key from the dashboard / 從控制台取得您的 API 金鑰</li>
                    <li>3. Paste it below and click Save / 貼上到下方欄位並點擊儲存</li>
                  </ol>
                </div>

                <div>
                  <Label className="text-white font-chinese">Your API Key (Optional) / 您的 API 金鑰（選填）</Label>
                  <div className="relative mt-2">
                    <Input
                      type={showSecrets.openweather_api_key ? 'text' : 'password'}
                      value={settings.openweather_api_key}
                      onChange={(e) => setSettings({ ...settings, openweather_api_key: e.target.value })}
                      className="bg-fac-dark border-border focus:border-fac-green text-white pr-12"
                      placeholder="Enter your OpenWeatherMap API key"
                      data-testid="weather-api-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowSecrets({ ...showSecrets, openweather_api_key: !showSecrets.openweather_api_key })}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-white"
                    >
                      {showSecrets.openweather_api_key ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <Button
                  onClick={() => handleSave('weather')}
                  disabled={saving || !settings.openweather_api_key}
                  className="bg-fac-green text-black font-bold"
                  data-testid="save-weather-key-btn"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Saving...' : 'Save API Key / 儲存 API 金鑰'}
                </Button>

                <div className="pt-4 border-t border-border">
                  <p className="text-sm text-muted-foreground font-chinese">
                    Weather Location / 天氣位置: <span className="text-white">Your Farm Location / 您的農場位置</span>
                    <br />
                    <span className="text-xs">Location auto-detected / 位置自動偵測</span>
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* FAC Cloud API Credentials */}
            <Card className="bg-fac-card border-border">
              <CardHeader>
                <CardTitle className="font-heading text-white uppercase flex items-center gap-2">
                  <Server className="w-5 h-5 text-fac-green" />
                  FAC Cloud API
                </CardTitle>
                <CardDescription className="font-chinese">
                  FAC 雲端 API 憑證 - Connect to real FAC Smart Valve devices
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-2 p-3 bg-fac-paper rounded-lg">
                  {facStatus?.connected ? (
                    <>
                      <CheckCircle className="w-5 h-5 text-fac-green" />
                      <span className="text-white">FAC Cloud Connected</span>
                      {facStatus.has_system_credentials && (
                        <span className="text-fac-green text-sm ml-2">(System credentials active)</span>
                      )}
                    </>
                  ) : (
                    <>
                      <XCircle className="w-5 h-5 text-fac-yellow" />
                      <span className="text-muted-foreground">FAC Cloud not connected</span>
                    </>
                  )}
                </div>

                <div className="p-4 bg-fac-paper border border-border rounded-lg">
                  <h4 className="text-white font-medium mb-2">Override with your own credentials / 使用您自己的憑證:</h4>
                  <p className="text-sm text-muted-foreground font-chinese">
                    System credentials are pre-configured. You can optionally add your own FAC App ID and Secret Key.
                    <br />
                    系統憑證已預先設定。您可以選擇性地新增您自己的 FAC App ID 和 Secret Key。
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white font-chinese">App ID (Optional)</Label>
                    <Input
                      type="text"
                      value={settings.fac_app_id}
                      onChange={(e) => setSettings({ ...settings, fac_app_id: e.target.value })}
                      className="mt-2 bg-fac-dark border-border focus:border-fac-green text-white"
                      placeholder="Your FAC App ID"
                      data-testid="fac-app-id-input"
                    />
                  </div>
                  <div>
                    <Label className="text-white font-chinese">App Secret Key (Optional)</Label>
                    <div className="relative mt-2">
                      <Input
                        type={showSecrets.fac_app_secret ? 'text' : 'password'}
                        value={settings.fac_app_secret}
                        onChange={(e) => setSettings({ ...settings, fac_app_secret: e.target.value })}
                        className="bg-fac-dark border-border focus:border-fac-green text-white pr-12"
                        placeholder="Your FAC Secret Key"
                        data-testid="fac-secret-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowSecrets({ ...showSecrets, fac_app_secret: !showSecrets.fac_app_secret })}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-white"
                      >
                        {showSecrets.fac_app_secret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button
                    onClick={() => handleSave('fac')}
                    disabled={saving || (!settings.fac_app_id && !settings.fac_app_secret)}
                    className="bg-fac-green text-black font-bold"
                    data-testid="save-fac-btn"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {saving ? 'Saving...' : 'Save Credentials / 儲存憑證'}
                  </Button>
                  
                  <Button
                    variant="outline"
                    onClick={testFacConnection}
                    className="border-fac-blue text-fac-blue hover:bg-fac-blue/10"
                    data-testid="test-fac-btn"
                  >
                    Test Connection / 測試連線
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* FAC Cloud Tab */}
          <TabsContent value="fac-cloud" className="space-y-6">
            {!isFacConnected ? (
              <Card className="bg-fac-card border-border">
                <CardContent className="p-8 text-center">
                  <Server className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                  <p className="text-xl text-white font-chinese mb-2">
                    FAC Cloud Not Connected / FAC 雲端未連線
                  </p>
                  <p className="text-muted-foreground font-chinese mb-4">
                    Please add your FAC API credentials in the "API Keys" tab first.
                    <br />
                    請先在「API Keys」標籤頁新增您的 FAC API 憑證。
                  </p>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* Connection Status */}
                <Card className="bg-fac-card border-border">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full ${facStatus?.connected ? 'bg-fac-green animate-pulse' : 'bg-fac-red'}`} />
                      <span className="text-white font-medium">
                        {facStatus?.connected ? 'Connected to FAC Cloud' : 'Disconnected'}
                      </span>
                      {facStatus?.cloud_host && (
                        <span className="text-muted-foreground text-sm ml-auto font-mono">
                          {facStatus.cloud_host}
                        </span>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Bind Device */}
                <Card className="bg-fac-card border-border">
                  <CardHeader>
                    <CardTitle className="font-heading text-white uppercase flex items-center gap-2">
                      <LinkIcon className="w-5 h-5 text-fac-blue" />
                      Add FAC Device / 新增 FAC 設備
                    </CardTitle>
                    <CardDescription className="font-chinese">
                      Enter your device serial number to add it to your dashboard
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-white font-chinese">Device Serial Number / 設備序號 *</Label>
                        <Input
                          type="text"
                          value={bindDeviceNo}
                          onChange={(e) => setBindDeviceNo(e.target.value)}
                          className="mt-2 bg-fac-dark border-border focus:border-fac-green text-white"
                          placeholder="e.g., FAC-001-TW"
                          data-testid="bind-device-no-input"
                        />
                      </div>
                      <div>
                        <Label className="text-white font-chinese">Device Name / 設備名稱 (Optional)</Label>
                        <Input
                          type="text"
                          value={bindDeviceName}
                          onChange={(e) => setBindDeviceName(e.target.value)}
                          className="mt-2 bg-fac-dark border-border focus:border-fac-green text-white"
                          placeholder="e.g., North Field Valve / 北田區閥門"
                          data-testid="bind-device-name-input"
                        />
                      </div>
                    </div>

                    <Button
                      onClick={handleBindDevice}
                      disabled={binding || !bindDeviceNo.trim()}
                      className="bg-fac-green text-black font-bold"
                      data-testid="bind-device-btn"
                    >
                      <LinkIcon className="w-4 h-4 mr-2" />
                      {binding ? 'Adding...' : 'Add Device / 新增設備'}
                    </Button>

                    <div className="p-4 bg-fac-paper border border-border rounded-lg">
                      <h4 className="text-white font-medium mb-2">Where to find your device serial number / 在哪裡找到設備序號:</h4>
                      <ul className="text-sm text-muted-foreground space-y-1 font-chinese">
                        <li>• Check the label on your FAC Smart Valve device / 查看 FAC 智慧閥門設備上的標籤</li>
                        <li>• Look in your FAC Cloud dashboard at smart1688.net / 在 smart1688.net 的 FAC 雲端控制台查看</li>
                        <li>• Contact FAC Solutions support / 聯絡 FAC 智慧方案客服</li>
                      </ul>
                    </div>
                  </CardContent>
                </Card>

                {/* Fetch Devices from Cloud */}
                <Card className="bg-fac-card border-border">
                  <CardHeader>
                    <CardTitle className="font-heading text-white uppercase flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Server className="w-5 h-5 text-fac-green" />
                        FAC Cloud Devices / FAC 雲端設備
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={fetchFacDevices}
                          disabled={loadingFacDevices}
                          className="border-fac-green text-fac-green hover:bg-fac-green/10"
                          data-testid="fetch-fac-devices-btn"
                        >
                          <RefreshCw className={`w-4 h-4 mr-2 ${loadingFacDevices ? 'animate-spin' : ''}`} />
                          {loadingFacDevices ? 'Loading...' : 'Fetch from Cloud'}
                        </Button>
                        <Button
                          size="sm"
                          onClick={async () => {
                            try {
                              const response = await axios.post(`${API}/fac/devices/sync`);
                              toast.success(`${response.data.message}`);
                              fetchFacDevices();
                            } catch (error) {
                              toast.error(error.response?.data?.detail || 'Sync failed');
                            }
                          }}
                          disabled={loadingFacDevices}
                          className="bg-fac-green text-black font-bold"
                          data-testid="sync-fac-devices-btn"
                        >
                          Sync All to App
                        </Button>
                      </div>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {facDevices.length === 0 ? (
                      <p className="text-center text-muted-foreground py-4 font-chinese">
                        Click "Fetch from Cloud" to search for devices associated with your account
                        <br />
                        點擊「Fetch from Cloud」搜尋與您帳戶關聯的設備
                      </p>
                    ) : (
                      <div className="space-y-3" data-testid="fac-devices-list">
                        {facDevices.map((device, index) => (
                          <div
                            key={device.fac_id || index}
                            className="flex items-center justify-between p-4 bg-fac-paper rounded-lg border border-border"
                          >
                            <div className="flex items-center gap-4">
                              {device.picture_url && (
                                <img 
                                  src={device.picture_url} 
                                  alt={device.name} 
                                  className="w-12 h-12 rounded object-cover"
                                />
                              )}
                              <div>
                                <p className="text-white font-medium">{device.name}</p>
                                <p className="text-sm text-muted-foreground font-mono">{device.serial_number}</p>
                                <div className="flex items-center gap-3 mt-1 flex-wrap">
                                  <span className={`text-xs px-2 py-0.5 rounded ${
                                    device.status === 'online' ? 'bg-fac-green/20 text-fac-green' : 'bg-fac-red/20 text-fac-red'
                                  }`}>
                                    {device.status === 'online' ? 'Online / 在線' : 'Offline / 離線'}
                                  </span>
                                  <span className="text-xs text-fac-blue">
                                    🔋 {device.battery}%
                                  </span>
                                  <span className="text-xs text-muted-foreground">
                                    {device.device_model}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <Button
                              size="sm"
                              onClick={() => {
                                setBindDeviceNo(device.serial_number);
                                setBindDeviceName(device.name);
                              }}
                              className="bg-fac-blue text-black"
                            >
                              <LinkIcon className="w-4 h-4 mr-1" />
                              Add
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default SettingsPage;
